import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminService } from '../../services/api';
import type { AdminTemplate } from '../../types';
import toast from 'react-hot-toast';
import {
  RefreshCw, Plus, Pencil, Trash2, GripVertical,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown
} from 'lucide-react';

const CATEGORIES = ['root', 'classic', 'modern', 'two-column', 'industry', 'creative', 'functional'];
const PAGE_SIZE = 10;

const PREVIEW_CSS_CARD =
  '<style>html,body{margin:0!important;padding:0!important;background:#fff!important;overflow:hidden!important}</style>';

const PREVIEW_CSS_SCROLL =
  '<style>html,body{margin:0!important;padding:0!important;background:#fff!important;overflow-y:auto!important}</style>';

const RESUME_API_URL = import.meta.env.VITE_RESUME_API_URL || 'https://3atech.de/api/v1/resumes';
const THUMB_ORIGIN = RESUME_API_URL.replace(/\/api\/v1\/resumes$/, '');
const getThumbUrl = (id: string) => `${THUMB_ORIGIN}/api/v1/resumes/thumbnails/${id}`;

const AdminTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<AdminTemplate | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [form, setForm] = useState({ id: '', name: '', category: 'root', content: '', isFree: false, isEnabled: true, sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);

  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(templates.length / PAGE_SIZE);
  const pageTemplates = templates.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminService.getTemplates();
      setTemplates(res.data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openEdit = (template: AdminTemplate) => {
    setEditingTemplate(template);
    setForm({
      id: template.id,
      name: template.name,
      category: template.category,
      content: template.content,
      isFree: template.isFree,
      isEnabled: template.isEnabled,
      sortOrder: template.sortOrder,
    });
    setIsCreateMode(false);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setForm({ id: '', name: '', category: 'root', content: '', isFree: false, isEnabled: true, sortOrder: 0 });
    setIsCreateMode(true);
  };

  const closeModal = () => { setEditingTemplate(null); setIsCreateMode(false); };

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim() || !form.category.trim() || !form.content.trim()) {
      toast.error('ID, name, category, and content are required');
      return;
    }
    setSaving(true);
    try {
      if (isCreateMode) {
        await adminService.createTemplate(form);
        toast.success('Template created');
      } else if (editingTemplate) {
        await adminService.updateTemplate(editingTemplate.id, form);
        toast.success('Template updated');
      }
      closeModal();
      await fetchTemplates();
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deleteTemplate(id);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      if (pageTemplates.length === 1 && page > 0) setPage(p => p - 1);
      await fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const handleToggle = async (template: AdminTemplate, field: 'isFree' | 'isEnabled') => {
    try {
      await adminService.updateTemplate(template.id, { [field]: !template[field] });
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, [field]: !t[field] } : t));
    } catch {
      toast.error('Failed to update template');
    }
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragId.current) setDragOverId(id);
  };

  const handleDragLeave = () => setDragOverId(null);

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;

    const sourceIdx = templates.findIndex(t => t.id === sourceId);
    const targetIdx = templates.findIndex(t => t.id === targetId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    const reordered = [...templates];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    const withOrder = reordered.map((t, i) => ({ ...t, sortOrder: i * 10 }));
    setTemplates(withOrder);

    try {
      await Promise.all([
        adminService.updateTemplate(sourceId, { sortOrder: withOrder[targetIdx].sortOrder }),
        adminService.updateTemplate(targetId, { sortOrder: withOrder[sourceIdx].sortOrder }),
      ]);
    } catch {
      toast.error('Failed to save new order');
      await fetchTemplates();
    }
  };

  const handleMove = async (templateId: string, direction: 'up' | 'down') => {
    const idx = templates.findIndex(t => t.id === templateId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= templates.length) return;

    const reordered = [...templates];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const withOrder = reordered.map((t, i) => ({ ...t, sortOrder: i * 10 }));
    setTemplates(withOrder);

    try {
      await Promise.all([
        adminService.updateTemplate(templateId, { sortOrder: withOrder[swapIdx].sortOrder }),
        adminService.updateTemplate(reordered[idx].id, { sortOrder: withOrder[idx].sortOrder }),
      ]);
      const targetPage = Math.floor(swapIdx / PAGE_SIZE);
      setPage(targetPage);
    } catch {
      toast.error('Failed to reorder');
      await fetchTemplates();
    }
  };

  if (isLoading) {
    return <div className="text-center p-12 text-secondary-foreground">Loading templates...</div>;
  }

  return (
    <div className="max-w-[1400px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground font-display">Resume Templates</h2>
          <p className="text-sm text-secondary-foreground">{templates.length} templates total — drag to reorder, arrows to move between pages</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchTemplates} className="h-9 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground hover:bg-subtle inline-flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={openCreate} className="h-9 px-4 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>
      </div>

      <div className="bg-white rounded-card shadow-soft">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse [&_tr:last-child_td]:border-b-0">
            <thead>
              <tr>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle w-10"></th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle w-16">Preview</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">ID</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Name</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Category</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm w-16">Free</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm w-20">Enabled</th>
                <th className="p-3 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageTemplates.map((t, localIdx) => {
                const globalIdx = page * PAGE_SIZE + localIdx;
                return (
                  <tr
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, t.id)}
                    onDragOver={(e) => handleDragOver(e, t.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, t.id)}
                    onDragEnd={() => { dragId.current = null; setDragOverId(null); }}
                    className={`hover:bg-subtle transition-colors ${dragOverId === t.id ? 'border-t-2 border-t-brand-500' : ''}`}
                  >
                    <td className="p-3 border-b border-zinc-100">
                      <GripVertical className="w-4 h-4 text-zinc-300 cursor-grab active:cursor-grabbing" />
                    </td>
                    <td className="p-3 border-b border-zinc-100">
                      <img
                        src={getThumbUrl(t.id)}
                        alt={t.name}
                        className="w-12 h-16 object-cover object-top border border-zinc-200 rounded"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </td>
                    <td className="p-3 border-b border-zinc-100 font-mono text-xs">{t.id}</td>
                    <td className="p-3 border-b border-zinc-100 text-sm font-medium">{t.name}</td>
                    <td className="p-3 border-b border-zinc-100">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
                        {t.category}
                      </span>
                    </td>
                    <td className="p-3 border-b border-zinc-100">
                      <button
                        onClick={() => handleToggle(t, 'isFree')}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.isFree ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${t.isFree ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="p-3 border-b border-zinc-100">
                      <button
                        onClick={() => handleToggle(t, 'isEnabled')}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.isEnabled ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${t.isEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="p-3 border-b border-zinc-100">
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleMove(t.id, 'up')}
                          disabled={globalIdx === 0}
                          className="h-7 w-7 flex items-center justify-center rounded-btn text-secondary-foreground hover:text-foreground hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move up"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMove(t.id, 'down')}
                          disabled={globalIdx === templates.length - 1}
                          className="h-7 w-7 flex items-center justify-center rounded-btn text-secondary-foreground hover:text-foreground hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move down"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(t)}
                          className="h-7 w-7 flex items-center justify-center rounded-btn text-secondary-foreground hover:text-foreground hover:bg-subtle transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {deleteConfirm === t.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDelete(t.id)} className="h-7 px-2 bg-red-500 text-white text-xs rounded-btn">Yes</button>
                            <button onClick={() => setDeleteConfirm(null)} className="h-7 px-2 bg-zinc-200 text-zinc-600 text-xs rounded-btn">No</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(t.id)}
                            className="h-7 w-7 flex items-center justify-center rounded-btn text-secondary-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100">
            <span className="text-sm text-secondary-foreground">
              Page {page + 1} of {totalPages} ({templates.length} templates)
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-8 w-8 flex items-center justify-center rounded-btn border border-zinc-200 hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`h-8 min-w-[32px] px-2 flex items-center justify-center rounded-btn text-sm transition-colors ${
                    i === page
                      ? 'bg-brand-500 text-white'
                      : 'border border-zinc-200 text-secondary-foreground hover:bg-subtle'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="h-8 w-8 flex items-center justify-center rounded-btn border border-zinc-200 hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {(editingTemplate || isCreateMode) && (
        <TemplateEditModal
          isCreateMode={isCreateMode}
          form={form}
          setForm={setForm}
          saving={saving}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

const TemplateEditModal: React.FC<{
  isCreateMode: boolean;
  form: { id: string; name: string; category: string; content: string; isFree: boolean; isEnabled: boolean; sortOrder: number };
  setForm: React.Dispatch<React.SetStateAction<{ id: string; name: string; category: string; content: string; isFree: boolean; isEnabled: boolean; sortOrder: number }>>;
  saving: boolean;
  onSave: () => void;
  onClose: () => void;
}> = ({ isCreateMode, form, setForm, saving, onSave, onClose }) => {
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPreview = useCallback((content: string) => {
    if (!content.trim()) { setPreviewHtml(null); return; }
    setPreviewing(true);
    adminService.previewTemplateContent(content)
      .then(res => setPreviewHtml(res.data))
      .catch(() => setPreviewHtml(null))
      .finally(() => setPreviewing(false));
  }, []);

  const handleContentChange = (newContent: string) => {
    setForm(prev => ({ ...prev, content: newContent }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreview(newContent), 500);
  };

  useEffect(() => {
    if (form.content.trim()) fetchPreview(form.content);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-card shadow-elevated w-full max-w-[1400px] h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <h2 className="text-xl font-semibold text-foreground font-display">
            {isCreateMode ? 'Create Template' : 'Edit Template'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-[420px] shrink-0 p-6 overflow-y-auto border-r border-zinc-100 space-y-4">
            {!isCreateMode && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Template ID</label>
                <input type="text" value={form.id} disabled className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn bg-zinc-50 text-secondary-foreground" />
              </div>
            )}
            {isCreateMode && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Template ID</label>
                <input
                  type="text" value={form.id}
                  onChange={(e) => setForm(prev => ({ ...prev, id: e.target.value }))}
                  placeholder="e.g. modern-creative"
                  className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Name</label>
                <input
                  type="text" value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none bg-white"
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-foreground">Free</label>
                <button
                  type="button" onClick={() => setForm(prev => ({ ...prev, isFree: !prev.isFree }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.isFree ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.isFree ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-foreground">Enabled</label>
                <button
                  type="button" onClick={() => setForm(prev => ({ ...prev, isEnabled: !prev.isEnabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.isEnabled ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.isEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <label className="text-sm font-medium text-foreground mb-1.5 block">HTML Content</label>
              <textarea
                value={form.content}
                onChange={(e) => handleContentChange(e.target.value)}
                className="flex-1 w-full px-3 py-2 text-xs font-mono border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none resize-none"
                style={{ minHeight: '300px' }}
                placeholder="Paste the full Thymeleaf HTML template here..."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={onSave} disabled={saving}
                className="h-10 px-6 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all">
                {saving ? 'Saving...' : isCreateMode ? 'Create' : 'Save'}
              </button>
              <button onClick={onClose}
                className="h-10 px-5 bg-white border border-zinc-200 rounded-btn text-sm text-foreground hover:bg-subtle">
                Cancel
              </button>
            </div>
          </div>

          <div className="flex-1 p-6 flex flex-col bg-zinc-50 overflow-hidden">
            <div className="flex items-center gap-2 mb-4 shrink-0">
              <h3 className="text-sm font-semibold text-foreground">Preview</h3>
              {previewing && <span className="text-xs text-secondary-foreground">Updating...</span>}
            </div>
            {previewHtml ? (
              <div className="flex-1 bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden">
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head>${PREVIEW_CSS_SCROLL}</head><body>${previewHtml}</body></html>`}
                  title="Template preview"
                  sandbox="allow-same-origin"
                  className="w-full h-full border-none"
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-zinc-400">
                {form.content.trim() ? 'Rendering preview...' : 'Enter HTML content to see preview'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTemplatesTab;
